import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import slaService, { type SLATicketStatus } from '@/services/sla.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Clock, Pause, Play, AlertTriangle, CheckCircle, XCircle, Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SLAWidgetProps {
  ticketId: string;
  compact?: boolean;
  flat?: boolean;
}

function formatTimeRemaining(seconds: number): string {
  if (seconds <= 0) {
    const absSeconds = Math.abs(seconds);
    const hours = Math.floor(absSeconds / 3600);
    const minutes = Math.floor((absSeconds % 3600) / 60);
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `-${days}d ${hours % 24}h`;
    }
    if (hours > 0) {
      return `-${hours}h ${minutes}m`;
    }
    return `-${minutes}m`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function getStatusColor(state: string): string {
  switch (state) {
    case 'active':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'responded':
    case 'resolved':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'paused':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'breached':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function getStatusIcon(state: string) {
  switch (state) {
    case 'active':
      return <Timer className="h-3 w-3" />;
    case 'responded':
    case 'resolved':
      return <CheckCircle className="h-3 w-3" />;
    case 'paused':
      return <Pause className="h-3 w-3" />;
    case 'breached':
      return <XCircle className="h-3 w-3" />;
    default:
      return <Clock className="h-3 w-3" />;
  }
}

function getStatusLabel(state: string): string {
  switch (state) {
    case 'active':
      return 'Aktiv';
    case 'responded':
      return 'Beantwortet';
    case 'resolved':
      return 'Gelöst';
    case 'paused':
      return 'Pausiert';
    case 'breached':
      return 'Überschritten';
    default:
      return state;
  }
}

export function SLAWidget({ ticketId, compact = false, flat = false }: SLAWidgetProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pauseReason, setPauseReason] = useState('');
  const [showPausePopover, setShowPausePopover] = useState(false);
  const [countdown, setCountdown] = useState({ firstResponse: 0, resolution: 0 });

  const { data: slaStatus, isLoading, isFetching } = useQuery({
    queryKey: ['sla-status', ticketId],
    queryFn: async () => {
      const response = await slaService.getTicketSLAStatus(ticketId);
      return response.data;
    },
    refetchInterval: (query) => {
      // SLA yoksa refetch yapma, varsa 60 saniyede bir güncelle
      return query.state.data ? 60000 : false;
    },
    retry: false, // SLA yoksa retry yapma
    staleTime: 30000, // 30 saniye cache
  });

  const pauseMutation = useMutation({
    mutationFn: ({ reason }: { reason?: string }) => slaService.pauseSLA(ticketId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(['sla-status', ticketId]);
      setShowPausePopover(false);
      setPauseReason('');
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () => slaService.resumeSLA(ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries(['sla-status', ticketId]);
    },
  });

  // Countdown timer
  useEffect(() => {
    if (!slaStatus || slaStatus.is_paused) return;

    setCountdown({
      firstResponse: slaStatus.first_response_remaining_seconds || 0,
      resolution: slaStatus.resolution_remaining_seconds || 0,
    });

    const interval = setInterval(() => {
      setCountdown(prev => ({
        firstResponse: prev.firstResponse > 0 ? prev.firstResponse - 1 : prev.firstResponse,
        resolution: prev.resolution > 0 ? prev.resolution - 1 : prev.resolution,
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, [slaStatus]);

  // Sadece ilk yüklemede skeleton göster, refetch sırasında gösterme
  // isLoading: ilk yükleme, isFetching: arka plan refetch
  if (isLoading && !slaStatus) {
    return (
      <div className="animate-pulse h-16 bg-slate-100 rounded-lg" />
    );
  }

  // SLA yoksa hiçbir şey gösterme
  if (!slaStatus) {
    return null;
  }

  const firstResponseState = slaStatus.current_first_response_state;
  const resolutionState = slaStatus.current_resolution_state;
  const isPaused = slaStatus.is_paused;

  // Compact mode için basit gösterim
  if (compact) {
    const isWarning = countdown.firstResponse <= 1800 && countdown.firstResponse > 0;
    const isCritical = countdown.firstResponse <= 0 && firstResponseState !== 'responded';

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className={`cursor-default ${
                isCritical
                  ? 'bg-red-100 text-red-700 border-red-200'
                  : isWarning
                  ? 'bg-amber-100 text-amber-700 border-amber-200'
                  : firstResponseState === 'responded'
                  ? 'bg-blue-100 text-blue-700 border-blue-200'
                  : isPaused
                  ? 'bg-amber-100 text-amber-700 border-amber-200'
                  : 'bg-emerald-100 text-emerald-700 border-emerald-200'
              }`}
            >
              {isPaused ? (
                <Pause className="h-3 w-3 mr-1" />
              ) : isCritical ? (
                <AlertTriangle className="h-3 w-3 mr-1" />
              ) : (
                <Clock className="h-3 w-3 mr-1" />
              )}
              {isPaused ? 'SLA Pausiert' : firstResponseState === 'responded'
                ? 'Beantwortet'
                : formatTimeRemaining(countdown.firstResponse)}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs">
            <div className="space-y-1 text-xs">
              <p className="font-medium">{slaStatus.policy_name}</p>
              <p>{t('common:x3.firstResponseColon', 'Erste Antwort:')} {getStatusLabel(firstResponseState)}</p>
              <p>{t('common:x3.resolutionColon', 'Lösung:')} {getStatusLabel(resolutionState)}</p>
              {isPaused && slaStatus.total_paused_duration > 0 && (
                <p className="text-amber-600">{t('common:x3.pausedColon', 'Pausiert:')} {slaStatus.total_paused_duration} Min.</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (flat) {
    return (
      <section className="ticket-info-section ticket-info-clean-section ticket-info-clean-section--sla ticket-sla-clean">
        <div className="ticket-info-section-header ticket-info-clean-heading ticket-sla-heading">
          <h3>
            <span className="ticket-info-section-icon ticket-info-section-icon--green" aria-hidden="true" />
            SLA
          </h3>
          <span className="ticket-sla-policy">{slaStatus.policy_name}</span>
        </div>

        <div className="ticket-sla-rows">
          <div className="ticket-sla-row">
            <span className="ticket-sla-label">{t('common:x1.firstResponse', 'Erste Antwort')}</span>
            <span className="ticket-sla-progress" aria-hidden="true">
              <span
                className={firstResponseState === 'responded' ? 'is-success' : countdown.firstResponse <= 0 ? 'is-danger' : countdown.firstResponse <= 1800 ? 'is-warning' : 'is-success'}
                style={{ width: firstResponseState === 'responded' ? '100%' : `${Math.max(0, Math.min(100, (countdown.firstResponse / (slaStatus.target_first_response_minutes * 60)) * 100))}%` }}
              />
            </span>
            <span className={`ticket-sla-state ${getStatusColor(firstResponseState)}`}>
              {getStatusIcon(firstResponseState)}
              {getStatusLabel(firstResponseState)}
            </span>
            {firstResponseState !== 'responded' && (
              <span className={countdown.firstResponse <= 0 ? 'ticket-sla-time is-danger' : 'ticket-sla-time'}>
                {formatTimeRemaining(countdown.firstResponse)}
              </span>
            )}
          </div>

          <div className="ticket-sla-row">
            <span className="ticket-sla-label">{t('common:x1.resolution', 'Lösung')}</span>
            <span className="ticket-sla-progress" aria-hidden="true">
              <span
                className={resolutionState === 'resolved' ? 'is-success' : countdown.resolution <= 0 ? 'is-danger' : countdown.resolution <= 3600 ? 'is-warning' : 'is-success'}
                style={{ width: resolutionState === 'resolved' ? '100%' : `${Math.max(0, Math.min(100, (countdown.resolution / (slaStatus.target_resolution_minutes * 60)) * 100))}%` }}
              />
            </span>
            <span className={`ticket-sla-state ${getStatusColor(resolutionState)}`}>
              {getStatusIcon(resolutionState)}
              {getStatusLabel(resolutionState)}
            </span>
            {resolutionState !== 'resolved' && (
              <span className={countdown.resolution <= 0 ? 'ticket-sla-time is-danger' : 'ticket-sla-time'}>
                {formatTimeRemaining(countdown.resolution)}
              </span>
            )}
          </div>
        </div>

        <div className="ticket-sla-footer">
          {slaStatus.total_paused_duration > 0 && (
            <span className="ticket-sla-paused">
              {t('common:x3.totalPaused', 'Gesamt pausiert:')} {slaStatus.total_paused_duration} Min.
            </span>
          )}
          {isPaused ? (
            <Button
              size="sm"
              variant="ghost"
              className="ticket-sla-action ticket-sla-action--resume"
              onClick={() => resumeMutation.mutate()}
              disabled={resumeMutation.isLoading}
            >
              <Play className="h-3 w-3" />
              {t('common:x3.resume', 'Fortsetzen')}
            </Button>
          ) : (
            <Popover open={showPausePopover} onOpenChange={setShowPausePopover}>
              <PopoverTrigger asChild>
                <Button size="sm" variant="ghost" className="ticket-sla-action">
                  <Pause className="h-3 w-3" />
                  {t('common:x1.pause', 'Pausieren')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="end">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-700">{t('common:x3.pauseSla', 'SLA pausieren')}</p>
                  <Input
                    placeholder={t('common:x3.reasonOptional', 'Grund (optional)')}
                    value={pauseReason}
                    onChange={(e) => setPauseReason(e.target.value)}
                    className="h-8 text-xs"
                  />
                  <Button
                    size="sm"
                    className="w-full h-7 text-xs bg-amber-500 hover:bg-amber-600"
                    onClick={() => pauseMutation.mutate({ reason: pauseReason })}
                    disabled={pauseMutation.isLoading}
                  >
                    {t('common:x1.pause', 'Pausieren')}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </section>
    );
  }

  // Full mode
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className={flat ? 'ticket-info-section-header ticket-info-clean-heading flex items-center justify-between' : 'bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center justify-between'}>
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
          {flat ? <span className="ticket-info-section-icon ticket-info-section-icon--green"><Clock className="h-3.5 w-3.5" /></span> : <Clock className="h-3.5 w-3.5" />}
          SLA
        </h3>
        <Badge variant="outline" className="ticket-info-status-badge text-[10px] px-1.5 py-0">
          {slaStatus.policy_name}
        </Badge>
      </div>

      <div className={flat ? 'ticket-info-clean-body space-y-3' : 'p-3 space-y-3'}>
        {/* First Response */}
        <div className={flat ? 'ticket-info-sla-metric space-y-1' : 'space-y-1'}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
              {t('common:x1.firstResponse', 'Erste Antwort')}
            </span>
            <Badge
              variant="outline"
              className={`ticket-info-status-badge text-[10px] px-1.5 py-0 ${getStatusColor(firstResponseState)}`}
            >
              {getStatusIcon(firstResponseState)}
              <span className="ml-1">{getStatusLabel(firstResponseState)}</span>
            </Badge>
          </div>

          {firstResponseState !== 'responded' && (
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    countdown.firstResponse <= 0
                      ? 'bg-red-500'
                      : countdown.firstResponse <= 1800
                      ? 'bg-amber-500'
                      : 'bg-emerald-500'
                  }`}
                  style={{
                    width: `${Math.max(0, Math.min(100, (countdown.firstResponse / (slaStatus.target_first_response_minutes * 60)) * 100))}%`,
                  }}
                />
              </div>
              <span className={`text-xs font-medium tabular-nums ${
                countdown.firstResponse <= 0 ? 'text-red-600' : 'text-slate-600'
              }`}>
                {formatTimeRemaining(countdown.firstResponse)}
              </span>
            </div>
          )}
        </div>

        {/* Resolution */}
        <div className={flat ? 'ticket-info-sla-metric space-y-1' : 'space-y-1'}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
              {t('common:x1.resolution', 'Lösung')}
            </span>
            <Badge
              variant="outline"
              className={`ticket-info-status-badge text-[10px] px-1.5 py-0 ${getStatusColor(resolutionState)}`}
            >
              {getStatusIcon(resolutionState)}
              <span className="ml-1">{getStatusLabel(resolutionState)}</span>
            </Badge>
          </div>

          {resolutionState !== 'resolved' && (
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    countdown.resolution <= 0
                      ? 'bg-red-500'
                      : countdown.resolution <= 3600
                      ? 'bg-amber-500'
                      : 'bg-emerald-500'
                  }`}
                  style={{
                    width: `${Math.max(0, Math.min(100, (countdown.resolution / (slaStatus.target_resolution_minutes * 60)) * 100))}%`,
                  }}
                />
              </div>
              <span className={`text-xs font-medium tabular-nums ${
                countdown.resolution <= 0 ? 'text-red-600' : 'text-slate-600'
              }`}>
                {formatTimeRemaining(countdown.resolution)}
              </span>
            </div>
          )}
        </div>

        {/* Pause/Resume Controls */}
        <div className={flat ? 'ticket-info-sla-actions flex items-center gap-2 pt-1' : 'flex items-center gap-2 pt-1'}>
          {isPaused ? (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-7 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              onClick={() => resumeMutation.mutate()}
              disabled={resumeMutation.isLoading}
            >
              <Play className="h-3 w-3 mr-1" />
              {t('common:x3.resume', 'Fortsetzen')}
            </Button>
          ) : (
            <Popover open={showPausePopover} onOpenChange={setShowPausePopover}>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 h-7 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                >
                  <Pause className="h-3 w-3 mr-1" />
                  {t('common:x1.pause', 'Pausieren')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="start">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-700">{t('common:x3.pauseSla', 'SLA pausieren')}</p>
                  <Input
                    placeholder={t('common:x3.reasonOptional', 'Grund (optional)')}
                    value={pauseReason}
                    onChange={(e) => setPauseReason(e.target.value)}
                    className="h-8 text-xs"
                  />
                  <Button
                    size="sm"
                    className="w-full h-7 text-xs bg-amber-500 hover:bg-amber-600"
                    onClick={() => pauseMutation.mutate({ reason: pauseReason })}
                    disabled={pauseMutation.isLoading}
                  >
                    {t('common:x1.pause', 'Pausieren')}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>

        {/* Paused duration info */}
        {slaStatus.total_paused_duration > 0 && (
          <p className="text-[10px] text-amber-600 text-center">
            {t('common:x3.totalPaused', 'Gesamt pausiert:')} {slaStatus.total_paused_duration} Minuten
          </p>
        )}
      </div>
    </div>
  );
}

export default SLAWidget;
